import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { PaletteIcon, ArrowRightIcon } from 'lucide-react';
export const ProfessionalLookPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5556">
      <Navbar scrolled={scrolled} data-id="element-5557" />
      <main className="pt-24 pb-20" data-id="element-5558">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5559">
          <div className="max-w-4xl mx-auto" data-id="element-5560">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5561">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5562">
                <PaletteIcon className="h-64 w-64" data-id="element-5563" />
              </div>
              <div className="relative z-10" data-id="element-5564">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5565">
                  Creating a Professional Look for Your Product
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5566">
                  Discover how to use layout, typography, and visual consistency
                  to present your product in a way that builds trust and
                  increases perceived value.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5567">
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5568">
                A professional presentation can significantly increase the
                perceived value of your product. Even a great product can be
                overlooked if it looks unpolished or inconsistent.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5569">
                Start with layout. Organize your content in a way that is easy
                to scan and understand. Use spacing, alignment, and structure to
                guide the viewer's attention.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5570">
                Typography plays a big role in how your product is perceived.
                Choose fonts that are clean and readable, and avoid using too
                many different styles. Consistency in font usage creates a more
                polished appearance.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5571">
                Visual consistency is key. Use a consistent color palette,
                style, and tone across your product materials. This includes
                thumbnails, previews, and any included assets.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5572">
                Make sure everything feels intentional. Small details like
                alignment, spacing, and visual balance can make a big
                difference. When everything looks aligned and well thought out,
                it builds trust with the buyer.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5573">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5574">
                  A professional look doesn't require complex design — it
                  requires clarity, consistency, and attention to detail.
                </p>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5575">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5576">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5577">
                <a href="/learn/designing-thumbnails" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5578">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5579">
                    Designing Thumbnails That Attract Buyers
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5580">
                    Create clean, eye-catching thumbnails that make your product
                    stand out.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5581">
                    <span data-id="element-5582">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5583" />
                  </div>
                </a>
                <a href="/learn/pricing-strategies" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5584">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5585">
                    How to Price Your Digital Products
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5586">
                    Choose the right pricing strategy based on value and
                    competition.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5587">
                    <span data-id="element-5588">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5589" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5590">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5591">
                Ready to elevate your product presentation?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5592">
                Apply these principles and present your products with
                confidence.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5593">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5594" />
    </div>;
};